import React from "react";
import { SimpleModulePage } from "./SimpleModulePage";
import { useAuth } from "../context/AuthContext";
import { DietModal } from "../components/DietModal";
import { config } from "../config/environment";
import { formatters } from "../utils/formatters";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string; onClick?: () => void; style?: React.CSSProperties }> = React.memo(
  ({ children, className = "", onClick, style }) => (
    <div 
      className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${className}`}
      onClick={onClick}
      style={style}
    >
      {children}
    </div>
  )
);

interface DietPageProps {
  onBack: () => void;
}

interface MealLog {
  id: string;
  meal_type: string;
  total_calories: number;
  total_protein: number;
  total_carbohydrates: number;
  total_fat: number;
  total_fiber: number;
  food_item_count: number;
  items: Array<{
    id: string;
    food_name: string;
    portion_size: string;
    calories: number;
    protein: number;
    carbohydrates: number;
    fat: number;
    ai_recognized: boolean;
    original_image_url?: string;
  }>;
}

interface DailySummary {
  breakfast_calories: number;
  lunch_calories: number;
  dinner_calories: number;
  snack_calories: number;
  total_daily_calories: number;
  total_protein: number;
  total_carbohydrates: number;
  total_fat: number;
  total_fiber: number;  // ✅ 식이섬유 추가
}

export const DietPage: React.FC<DietPageProps> = React.memo(({ onBack }) => {
  const { isLoggedIn } = useAuth();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedMealType, setSelectedMealType] = React.useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('breakfast');
  // ⚠️ 로컬 날짜 기준 (KST) - toISOString() 사용 금지
  const [selectedDate, setSelectedDate] = React.useState<string>(formatters.getLocalDateString());
  const [mealLogs, setMealLogs] = React.useState<MealLog[]>([]);
  const [dailySummary, setDailySummary] = React.useState<DailySummary | null>(null);
  const [selectedMealLog, setSelectedMealLog] = React.useState<MealLog | null>(null);
  const [showDetailModal, setShowDetailModal] = React.useState(false);
  const [isDeletingItem, setIsDeletingItem] = React.useState(false);

  const targetKcal = 1700;

  // 안전한 숫자 변환 함수
  const safeNumber = (value: any): number => {
    const num = parseFloat(String(value));
    return isNaN(num) ? 0 : num;
  };

  const totalIntake = safeNumber(dailySummary?.total_daily_calories);
  const remainingKcal = Math.max(0, targetKcal - totalIntake);
  const progressPercentage = Math.min(100, (totalIntake / targetKcal) * 100);

  const mealLabels: Record<string, string> = {
    breakfast: '아침',
    lunch: '점심',
    dinner: '저녁',
    snack: '간식'
  };

  const mealEmojis: Record<string, string> = {
    breakfast: '🌅',
    lunch: '☀️',
    dinner: '🌙',
    snack: '🍪'
  };

  // 페이지 진입 시 스크롤 상단으로 초기화
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 식단 데이터 로드
  const loadDietData = React.useCallback(async () => {
    if (!isLoggedIn) return;
    setIsRefreshing(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/diet/logs/${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // ✅ food_item_count가 없으면 items.length로 계산
          const processedLogs = (data.logs || []).map((meal: any) => ({
            ...meal,
            food_item_count: meal.food_item_count ?? meal.items?.length ?? 0
          }));
          
          if (process.env.NODE_ENV === 'development') {
            console.log('[DietPage] Loaded meal logs:', processedLogs);
            console.log('[DietPage] Daily summary:', data.diet);
          }
          
          setMealLogs(processedLogs);
          setDailySummary(data.diet || null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch diet data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isLoggedIn, selectedDate]);

  React.useEffect(() => {
    if (!isLoggedIn) return;
    loadDietData();
  }, [selectedDate, isLoggedIn, loadDietData]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  const handleAddMeal = (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack') => {
    setSelectedMealType(mealType);
    setIsModalOpen(true);
  };

  const handleMealClick = (meal: MealLog) => {
    setSelectedMealLog(meal);
    setShowDetailModal(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!window.confirm('이 음식을 삭제하시겠습니까?')) {
      return;
    }

    setIsDeletingItem(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/diet/item/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // ✅ 삭제 후 데이터 새로고침
          await loadDietData();
          
          // ✅ 선택된 식단도 업데이트 (모달이 열려있을 경우)
          if (selectedMealLog) {
            const updatedMeal = selectedMealLog.items.find(m => m.id === itemId);
            if (updatedMeal) {
              // 모달 내의 아이템 업데이트
              const updatedItems = selectedMealLog.items.filter(m => m.id !== itemId);
              setSelectedMealLog({
                ...selectedMealLog,
                items: updatedItems,
                total_calories: selectedMealLog.total_calories - (updatedMeal.calories || 0),
                total_protein: selectedMealLog.total_protein - (updatedMeal.protein || 0),
                total_carbohydrates: selectedMealLog.total_carbohydrates - (updatedMeal.carbohydrates || 0),
                total_fat: selectedMealLog.total_fat - (updatedMeal.fat || 0),
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to delete diet item:', error);
      alert('음식 삭제에 실패했습니다.');
    } finally {
      setIsDeletingItem(false);
    }
  };

  const handleModalSubmit = async () => {
    await loadDietData();
    setIsModalOpen(false);
  };

  return (
    <>
      <SimpleModulePage title="식단" subtitle="(먹은것기록)" icon="🍽️" onBack={onBack}>
        {/* 날짜 선택 */}
        <SectionCard>
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-semibold text-slate-600">날짜</span>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="text-[12px] px-2 py-1 border border-slate-200 rounded"
            />
          </div>
        </SectionCard>

        {/* 오늘 섭취 요약 */}
        <SectionCard>
          <p className="text-[12px] font-semibold text-slate-900 mb-2">오늘 섭취</p>
          <div className="flex items-end justify-between mb-2">
            <p className="text-[32px] font-bold text-slate-900">{Math.round(totalIntake)}</p>
            <p className="text-[12px] text-slate-600">/ {targetKcal}kcal</p>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
            <div 
              className="bg-gradient-to-r from-amber-400 to-orange-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <p className="text-[12px] text-slate-600">
            목표까지 <span className="font-semibold text-amber-600">{Math.round(remainingKcal)}</span>kcal 남았어요
          </p>

          {/* 영양소 요약 */}
          {dailySummary && (
            <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-100">
              <div className="text-center">
                <p className="text-[10px] text-slate-500">단백질</p>
                <p className="text-[14px] font-semibold text-slate-900">{Math.round(safeNumber(dailySummary.total_protein))}</p>
                <p className="text-[9px] text-slate-400">g</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-slate-500">탄수화물</p>
                <p className="text-[14px] font-semibold text-slate-900">{Math.round(safeNumber(dailySummary.total_carbohydrates))}</p>
                <p className="text-[9px] text-slate-400">g</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-slate-500">지방</p>
                <p className="text-[14px] font-semibold text-slate-900">{Math.round(safeNumber(dailySummary.total_fat))}</p>
                <p className="text-[9px] text-slate-400">g</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-slate-500">식이섬유</p>
                <p className="text-[14px] font-semibold text-slate-900">{Math.round(safeNumber(dailySummary.total_fiber))}</p>
                <p className="text-[9px] text-slate-400">g</p>
              </div>
            </div>
          )}
        </SectionCard>

        {/* 식단 기록 카드 */}
        <div className="space-y-0">
          {Object.keys(mealLabels).map((mealType: any) => {
            const meal = mealLogs.find(m => m.meal_type === mealType);
            const mealCalories = safeNumber(meal?.total_calories);
            // ✅ food_item_count: meal.items.length로 자동 계산
            const itemCount = meal?.items?.length || meal?.food_item_count || 0;
            
            return (
              <SectionCard
                key={mealType}
                onClick={() => meal && handleMealClick(meal)}
                style={{ borderLeft: meal ? '4px solid rgb(251 146 60)' : 'none' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-[20px]">{mealEmojis[mealType]}</span>
                    <span className="text-[13px] font-semibold text-slate-700">{mealLabels[mealType]}</span>
                    {meal && itemCount > 0 && <span className="text-[11px] text-slate-500">({itemCount}개)</span>}
                  </div>
                  <div className="text-right">
                    <p className="text-[14px] font-bold text-slate-900">{Math.round(mealCalories)}</p>
                    <p className="text-[10px] text-slate-500">kcal</p>
                  </div>
                </div>

                {meal && meal.items.length > 0 ? (
                  <>
                    <div className="space-y-1 mb-2">
                      {meal.items.slice(0, 2).map((item) => (
                        <div key={item.id} className="flex justify-between">
                          <p className="text-[11px] text-slate-600">{item.food_name}</p>
                          <p className="text-[11px] font-semibold text-slate-700">{Math.round(safeNumber(item.calories))}kcal</p>
                        </div>
                      ))}
                      {meal.items.length > 2 && (
                        <p className="text-[10px] text-slate-500">외 {meal.items.length - 2}개 항목</p>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400">자세히 보기 →</p>
                  </>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddMeal(mealType);
                    }}
                    className="text-[12px] text-blue-500 font-semibold hover:text-blue-600"
                  >
                    + 식단 기록 추가
                  </button>
                )}
              </SectionCard>
            );
          })}
        </div>

        {isRefreshing && (
          <SectionCard className="text-center">
            <p className="text-[12px] text-slate-500">로딩 중...</p>
          </SectionCard>
        )}
      </SimpleModulePage>

      {/* 식단 기록 모달 */}
      <DietModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMealType('breakfast'); // 모달 닫을 때 초기화
        }}
        onSubmit={handleModalSubmit}
        mealType={selectedMealType}
        selectedDate={selectedDate}
      />

      {/* 상세 보기 모달 */}
      {showDetailModal && selectedMealLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50 animate-fadeIn">
          <div className="w-full bg-white rounded-t-3xl p-4 max-h-[80vh] overflow-y-auto animate-slideUp">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[16px] font-bold text-slate-900">
                {mealLabels[selectedMealLog.meal_type]} 상세
              </h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-[24px] text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            {/* 식단 항목 목록 */}
            <div className="space-y-2 mb-4">
              {selectedMealLog.items.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-start p-3 bg-slate-50 rounded-lg border border-slate-100"
                >
                  <div className="flex-1">
                    <p className="text-[13px] font-semibold text-slate-900">{item.food_name}</p>
                    {item.portion_size && (
                      <p className="text-[11px] text-slate-500">{item.portion_size}</p>
                    )}
                    {item.ai_recognized && (
                      <span className="text-[9px] bg-blue-100 text-blue-700 px-2 py-1 rounded mt-1 inline-block">
                        AI 인식됨
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-[13px] font-bold text-slate-900">{Math.round(item.calories || 0)}</p>
                      <p className="text-[10px] text-slate-500">kcal</p>
                    </div>
                    {/* ✅ 삭제 버튼 */}
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      disabled={isDeletingItem}
                      className="ml-2 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                      title="삭제"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* 영양소 합계 */}
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <p className="text-[12px] font-semibold text-slate-900 mb-2">영양소 정보</p>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <p className="text-[10px] text-slate-500">단백질</p>
                  <p className="text-[13px] font-semibold text-slate-900">{Math.round(selectedMealLog.total_protein || 0)}g</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500">탄수화물</p>
                  <p className="text-[13px] font-semibold text-slate-900">{Math.round(selectedMealLog.total_carbohydrates || 0)}g</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500">지방</p>
                  <p className="text-[13px] font-semibold text-slate-900">{Math.round(selectedMealLog.total_fat || 0)}g</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500">식이섬유</p>
                  <p className="text-[13px] font-semibold text-slate-900">{Math.round(selectedMealLog.total_fiber || 0)}g</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowDetailModal(false);
                setSelectedMealType(selectedMealLog.meal_type as any);
                setIsModalOpen(true);
              }}
              className="w-full mt-4 bg-blue-500 text-white py-2 rounded-lg font-semibold text-[13px] hover:bg-blue-600"
            >
              + 음식 추가
            </button>
          </div>
        </div>
      )}
    </>
  );
});
